/**
 * Per-session feature flags, persisted to localStorage.
 *
 * Flags are boolean-only for now. The set of known flags is declared here so
 * each flag has an explicit default and description. Values can be toggled
 * from the nav bar; they take effect immediately.
 *
 * The hook uses useSyncExternalStore with a CACHED snapshot — callers must
 * receive the same reference when nothing has changed, otherwise React
 * infinite-re-renders.
 */
import { useSyncExternalStore } from "react";

export type FlagKey = "strictUmlauts" | "debugScheduler" | "autoAdvance";

export const FLAGS: Record<
  FlagKey,
  { label: string; description: string; default: boolean }
> = {
  strictUmlauts: {
    label: "Strict umlauts",
    description:
      'Reject answers missing umlauts / ß. When off (default), "grun" is accepted for "grün" but the canonical spelling is shown in feedback.',
    default: false,
  },
  debugScheduler: {
    label: "Scheduler debug overlay",
    description:
      "On each exercise, show the agent's internal reasoning — value function, full action space, and per-candidate Q̂ — above the prompt.",
    default: false,
  },
  autoAdvance: {
    label: "Auto-advance to next question",
    description:
      "After you answer, show the feedback briefly and then move on automatically (2s if correct, 4s if wrong). Tap anywhere or press a key to cancel; the manual Next button still works.",
    default: false,
  },
};

const STORAGE_KEY = "lingua.featureFlags";

function load(): Record<FlagKey, boolean> {
  const result: Record<string, boolean> = {};
  try {
    const raw =
      typeof localStorage !== "undefined"
        ? localStorage.getItem(STORAGE_KEY)
        : null;
    const parsed = raw ? JSON.parse(raw) : {};
    for (const key of Object.keys(FLAGS) as FlagKey[]) {
      result[key] =
        typeof parsed[key] === "boolean" ? parsed[key] : FLAGS[key].default;
    }
  } catch {
    for (const key of Object.keys(FLAGS) as FlagKey[]) {
      result[key] = FLAGS[key].default;
    }
  }
  return result as Record<FlagKey, boolean>;
}

// Cached snapshot. useSyncExternalStore requires the snapshot to be
// referentially stable between changes.
let snapshot: Record<FlagKey, boolean> = load();
const subscribers = new Set<() => void>();

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) {
      snapshot = load();
      for (const cb of subscribers) cb();
    }
  });
}

function subscribe(cb: () => void) {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

function getSnapshot() {
  return snapshot;
}

export function useFlags(): Record<FlagKey, boolean> {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useFlag(key: FlagKey): boolean {
  return useFlags()[key];
}

export function setFlag(key: FlagKey, value: boolean) {
  if (snapshot[key] === value) return;
  snapshot = { ...snapshot, [key]: value };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* ignore quota/availability */
  }
  for (const cb of subscribers) cb();
}
